import React, { useRef, useEffect, useCallback, useMemo, Dispatch, SetStateAction } from 'react'
import { a, useSpring } from '@react-spring/web'
import { useDrag, useInputContext, debounce, colord, useTh, Components, mergeRefs } from 'leva/plugin'
import { Canvas, SpringPreview } from '../styles/StyledSpring'
import type { BounceProps, CubicBounceLerp } from '../types/bounce-types'
import { useCanvas2d } from '../utilities/useCanvas'
import { StyledBounce } from '../styles/StyledBounce'
import { BouncePreview } from './BouncePreview'
import { bounceScalingCutoff, displayGridIncrement } from '../assets/constants'
import { rangeMap } from '../utilities/bezier-utils'
const { Row } = Components

const SpringPreviewAnimated = a(SpringPreview)

export function BounceCanvas(props: { setBounceValues: Dispatch<SetStateAction<any[]>> }) {
  const { displayValue, value, onUpdate, settings } = useInputContext<BounceProps>()
  const springRef = useRef(displayValue)
  const accentColor = useTh('colors', 'highlight3')
  const backgroundColor = useTh('colors', 'elevation2')
  const fillColor = useTh('colors', 'highlight1')
  const [displayWidth, setDisplayWidth] = React.useState(0)
  const [displayHeight, setDisplayHeight] = React.useState(0)
  const [ newBounces, setNewBounces ] = React.useState<CubicBounceLerp[]>([])
  const [gradientTop, gradientBottom] = useMemo(() => {
    return [colord(fillColor).alpha(0.4).toRgbString(), colord(fillColor).alpha(0.1).toRgbString()]
  }, [fillColor])

  const { elasticity, gravity, hang, velocity, decayY, decayX  } = displayValue
  const { elasticity: ts, gravity: fs } = settings!

  const [spring, api] = useSpring(() => ({
    scaleX: 0.5,
    opacity: 0.2,
    immediate: (k) => k === 'opacity',
  }))

  const bind = useDrag(({ movement: [x, y], memo = [elasticity, gravity] }) => {
    onUpdate({
      ...value,
      elasticity: memo[0] - Math.round(x) * ts.step,
      gravity: memo[1] - Math.round(y / 4) * fs.step,
    })
    return memo
  })

  //@ts-ignore
  function scale (number, inMin, inMax, outMin, outMax) {
    return (number - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
  }

  //@ts-ignore
  const createMirroredBezier = function(ognHandle, ogn, peakHandle, peak, dims, pathObj) {
        let { ognX, ognY } = ogn
        let { ognHandleX, ognHandleY } = ognHandle
        let { peakX, peakY } = peak
        let { peakHandleX, peakHandleY } = peakHandle
        pathObj.moveTo(ognX, ognY)
        pathObj.bezierCurveTo(ognHandleX, ognHandleY, peakHandleX, peakHandleY, peakX, peakY)
        pathObj.moveTo(peakX, peakY)
        let arcXLength = (peakX - ognX) * 2
        let originHandleXDifference = ognX - ognHandleX
        let peakHandleXDifference = peakX - peakHandleX
        let dstX = ognX + arcXLength
        let mirroredOgnHandleX = dstX + originHandleXDifference  
        let mirroredPeakHandleX = peakX + peakHandleXDifference
        pathObj.bezierCurveTo(mirroredPeakHandleX, peakHandleY, mirroredOgnHandleX, ognHandleY, dstX, ognY)


        let ognXCubic = scale(ognHandleX, ognX, peakX, 0, 1)
        let ognYCubic = scale(ognHandleY, ognY, peakY, 0, 1)
        let peakXCubic = scale(peakHandleX, ognX, peakX, 0, 1)
        let peakYCubic = scale(peakHandleY, ognY, peakY, 0, 1)
        let ognLerpX = scale(ognX, 0, dims.width, 0, 1)
        let peakLerpY = scale(peakY, 0, dims.height, 0, 1)
        let dstLerpX = scale(dstX, 0, dims.width, 0, 1)
        
        let cubicBounceLerp: CubicBounceLerp = {
          ognXCubic: 1 - ognXCubic,
          ognYCubic: 1 - ognYCubic,
          peakXCubic,
          peakYCubic,
          mirrorPeakXCubic: 1 - ognYCubic,
          mirrorPeakYCubic: 1 - ognXCubic,
          dstXCubic: peakXCubic,
          dstYCubic: peakYCubic,
          ognLerpX,
          peakLerpY,
          dstLerpX
        }
        return cubicBounceLerp
  }

  // const updateSpring = useMemo(
  //   () =>
  //     debounce(() => {
  //       const { elasticity, gravity, hang, velocity, decayY, decayX  } = springRef.current
  //       api.start({
  //         //@ts-ignore
  //         from: { scaleX: 0, opacity: 0.9 },
  //         to: [{ scaleX: 0.5 }, { opacity: 0.2 }],
  //         config: { elasticity, gravity, hang, velocity, decayY, decayX  },
  //       })
  //     }, 250),
  //   [api]
  // )

  const drawSpring = useCallback(
    (_canvas: HTMLCanvasElement, _ctx: CanvasRenderingContext2D) => {
      let { elasticity, gravity, hang, velocity, decayY, decayX } = springRef.current
      const path = new Path2D()
      const { width, height } = _canvas
      setDisplayWidth(width)
      setDisplayHeight(height)
      elasticity = rangeMap([1, 100], [.3, 1])(elasticity)
      gravity = rangeMap([100, 1], [.01, 4])(gravity) 
      hang = rangeMap([0, 50], [1.5, 1])(hang)
      velocity = rangeMap([1, 100], [.01, 2])(velocity)
      decayX = rangeMap([100, 1], [.01, 1])(decayX)
      decayY = rangeMap([100, 1], [.01, 1])(decayY)
      let bounces: any[] = []
      let offset = width/5
      let ognX = offset
      let ognY = 0
      let ognHandleX = ((width / 40) * gravity) * elasticity + offset
      let ognHandleY = ((height / 3) * 1) * elasticity / hang * velocity
      let peakX = (((width / 9.5) * gravity) * (elasticity )) + offset
      let peakY = ((height / 1.5) * 1) * (elasticity) * velocity
      let peakHandleX = (((width /15) * gravity) * hang) * (elasticity ) + offset
      let peakHandleY = peakY 

      path.moveTo(ognX, 0) 
      for (let i = 0; i < 10; i++) {
        if (peakY - ognY < (height*bounceScalingCutoff)) break
        let bounce = createMirroredBezier({ognHandleX: ognHandleX, ognHandleY: ognHandleY}, 
                            {ognX: ognX, ognY: 0}, 
                            {peakHandleX: peakHandleX, peakHandleY: peakHandleY}, 
                            {peakX: peakX, peakY: peakY},
                            {width: width, height: height}, 
                            path)
        
        bounces.push(bounce)
        path.moveTo(ognX, 0)
        path.lineTo(ognHandleX, ognHandleY)
        path.moveTo(ognX, 0)
        path.moveTo(peakX, peakY)
        path.lineTo(peakHandleX, peakHandleY)
        decayX *= elasticity
        decayY *= elasticity
        let ognXHandleDistance = (ognHandleX - ognX) * decayX
        let peakXHandleDistance = ((peakX - peakHandleX)) * decayX
        let bounceLength = (peakX - ognX) * 2

        ognX = (ognX + bounceLength)
        peakX = (ognX + ((bounceLength * decayX) / (2)))
        peakY *= decayY
        ognHandleY *= decayY
        ognHandleX = (ognX + ognXHandleDistance)
        peakHandleY = peakY
        peakHandleX = (peakX - peakXHandleDistance)
      }
      props.setBounceValues(bounces)
      setNewBounces(bounces)
      // clear
      _ctx.clearRect(0, 0, width, height)
      //background grid
      _ctx.strokeStyle = 'white';
      _ctx.lineWidth = 1

      _ctx.globalAlpha = 1;
      // draw the dark line
      _ctx.strokeStyle = backgroundColor
      _ctx.lineJoin = 'round'
      _ctx.lineWidth = 14
      _ctx.stroke(path)

      // draw the white line
      _ctx.strokeStyle = accentColor
      _ctx.lineWidth = 2
      _ctx.stroke(path)
      gridLines(_canvas!, _ctx!)
      drawDashed(canvas.current!, ctx.current!)

    },
    [accentColor, backgroundColor, gradientTop, gradientBottom]
  )

  

  const [canvas, ctx] = useCanvas2d(drawSpring)
  function drawDashed(_canvas: any, _ctx: any) {
    const { width, height } = _canvas
      const path = new Path2D()
      _ctx.lineWidth = 3
      _ctx.setLineDash([6, 12]);
      path.moveTo(0, height)
      path.lineTo(width/5, 0)
      _ctx.stroke(path)
      _ctx.setLineDash([]);
  }

  //@ts-ignore
  const gridLines = (_canvas: any, _ctx: any) => {
    let { width, height } = _canvas
    const rect = _canvas.getBoundingClientRect();
    const path = new Path2D()
    //multiplier to handle discrepancy between canvas size and display size
    let xMultiplier = width / rect.width
    let yMultiplier = height / rect.height
    let numXLines = Math.floor(rect.width / displayGridIncrement)
    let xRemainder = rect.width % displayGridIncrement * xMultiplier
    let xIncrementStretch = xRemainder ? xRemainder / numXLines : 0
    let numYLines = Math.floor(rect.height / displayGridIncrement)
    let yRemainder = rect.height % displayGridIncrement * yMultiplier
    let yIncrementStretch = yRemainder ? yRemainder / numYLines : 0
    _ctx.lineWidth = 1
    _ctx.globalAlpha = 0.16

    for (let i = 0; i <= numXLines; i++) {
      let x = i * (displayGridIncrement * xMultiplier) + xIncrementStretch * i
      path.moveTo(x, height)
      path.lineTo(x, 0)
    }
    for (let i = 0; i <= numYLines; i++) {
      let y = i * (displayGridIncrement * yMultiplier) + yIncrementStretch * i
      path.moveTo(width, y)
      path.lineTo(0, y)
    }
    _ctx.stroke(path)
  } 

  useEffect(() => {
      // @ts-ignore
    springRef.current = { elasticity, gravity, hang, velocity, decayY, decayX }
    drawSpring(canvas.current!, ctx.current!)
  }, [drawSpring, elasticity, gravity, hang, velocity, decayY, decayX, canvas, ctx])

  return (
    <>
      <StyledBounce>
        <Canvas  ref={canvas} style={{  width: '100%', height: '100%', position: 'absolute', top: 0, left: 0,}} />
      </StyledBounce>
      <BouncePreview bounceValues={newBounces} width={displayWidth} height={displayHeight}/>
    </>
  )
}